import { useCallback, useState } from 'react';
import { ValidationError } from '@/shared/lib/validation-error';

type FieldBinding<V> = {
  readonly value: V;
  readonly onChange: (value: V) => void;
  readonly error: string | undefined;
};

/** Form values plus per-field errors mapped from a ValidationError thrown while saving. */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [saving, setSaving] = useState(false);

  const set = useCallback(function setField<K extends keyof T>(key: K, value: T[K]) {
    setValues((current) => ({ ...current, [key]: value }));
    setErrors((current) => (current[key as string] ? { ...current, [key]: undefined } : current));
  }, []);

  function field<K extends keyof T>(key: K): FieldBinding<T[K]> {
    return { value: values[key], onChange: (value) => set(key, value), error: errors[key as string] };
  }

  async function submit(save: () => Promise<void>): Promise<boolean> {
    setSaving(true);
    try {
      await save();
      return true;
    } catch (error) {
      if (!(error instanceof ValidationError)) throw error;
      const next: Record<string, string> = {};
      for (const issue of error.issues) next[String(issue.path[0] ?? '')] ??= issue.message;
      setErrors(next);
      return false;
    } finally {
      setSaving(false);
    }
  }

  return { values, set, field, errors, saving, submit };
}
